import { createAction, props } from '@ngrx/store';
import { Action, ActionAlert, Instruction, ItemProperties, Row } from 'src/app/types/aggregate-graph.types';
import { SheetActionNote, SheetGranularity, SheetTemplate, SheetViewSelector, SheetViewStatus } from 'src/app/types/sheet.types';

export enum SheetActionTypes {
	SheetLoadInitiated = '[Effect] Sheet Load Initiated',
	SelectedVisitChanged = '[Effect] Selected Visit Changed',
	LoadSheetItemEditorRequested = '[Service] Load Sheet Item Editor Requested',
	LoadSheetItemEditorCompleted = '[Effect] Load Sheet Item Editor Completed',
	AddSheetItemInstructionsRequested = '[Modal] Add Sheet Item Instructions Requested',
	AddSheetItemInstructionsCompleted = '[Effect] Add Sheet Item Instructions Completed',
	UpdateSheetItemInstructionsRequested = '[Modal] Update Sheet Item Instructions Requested',
	UpdateSheetItemInstructionsCompleted = '[Effect] Update Sheet Item Instructions Completed',
	LoadSheetSlotEditorRequested = '[Service] Load Sheet Slot Editor Requested',
	LoadSheetSlotEditorCompleted = '[Effect] Load Sheet Slot Editor Completed',
	SaveSheetSlotActionRequested = '[Modal] Save Sheet Slot Action Requested',
	AddSheetSlotActionCompleted = '[Effect] Add Sheet Slot Action Completed',
	UpdateSheetSlotActionCompleted = '[Effect] Update Sheet Slot Action Completed',
	SelectDayRequested = '[Component] Select Day Requested',
	SelectDayCompleted = '[Effect] Select Day Completed',
	UpdateScheduledUntilDateRequested = '[Effect] Update Scheduled Until Date Requested',
	UpdateScheduledUntilDateCompleted = '[Effect] Update Scheduled Until Date Completed',
	ChangeViewStatusRequested = '[Component] Change View Status Requested',
	ChangeViewStatusCompleted = '[Component] Change View Status Completed',
	LoadSheetActionNotesRequested = '[Effect] Load Sheet Action Notes Requested',
	LoadSheetActionNotesCompleted = '[Effect] Load Sheet Action Notes Completed',
	SheetActionNotesCleared = '[Effect] Sheet Action Notes Cleared',
	SetItemPropertiesRequested = '[Effect] Set Item Properties Requested',
	SetItemPropertiesCompleted = '[Effect] Set Item Properties Completed',
	UpdateSheetItemActionAlertsRequested = '[Modal] Update Sheet Item Action Alerts Requested',
	UpdateSheetItemActionAlertsCompleted = '[Effect] Update Sheet Item Action Alerts Completed',
	ChangeGranularityRequested = '[Component] Change Granularity Requested',
	ChangeGranularityCompleted = '[Effect] Change Granularity Completed',
	ExportSheetRequested = '[Sheet Graph Effect] Export Sheet Requested',
	ExportSheetCompleted = '[Sheet Graph Effect] Export Sheet Completed',
	RemoveSheetItemRequested = '[Component] Remove Sheet Item Requested',
	RemoveSheetItemCompleted = '[Effect] Remove Sheet Item Completed',
	PresentCreateSheetModalRequested = '[Component] Present Create Sheet Modal Requested',
	CreateSheetTemplateFromSheetRequested = '[Component] Create Sheet Template From Sheet Requested',
	CreateSheetTemplateFromSheetCompleted = '[Effect] Create Sheet Template From Sheet Completed',
	PresentCreateSheetTemplateModal = '[Component] Present Create Sheet Template Modal',
	PresentManageSheetTemplatesModalRequested = '[Component] Present Manage Sheet Templates Modal Requested',
	PresentItemSelectorRequested = '[Component] Present Item Selector Requested',
	PresentItemSearchModalAllowed = '[Component] Present Item Search Modal Allowed',
	FinalisedSheetEditAttempted = '[Effect] Finalised Sheet Edit Attempted',
	SheetCleared = '[Effect] Sheet Cleared'
}

export const sheetLoadInitiated = createAction(
	SheetActionTypes.SheetLoadInitiated,
	props<{ visitId: string, sheetId: string | null }>()
)

export const selectedVisitChanged = createAction(
	SheetActionTypes.SelectedVisitChanged,
	props<{ visitId: string, sheetId: string | null }>()
)

export const exportSheetRequested = createAction(
	SheetActionTypes.ExportSheetRequested
)

export const finalisedSheetEditAttempted = createAction(
	SheetActionTypes.FinalisedSheetEditAttempted,
	props<{ messageOverride: string | undefined }>()
)

export const exportSheetCompleted = createAction(
	SheetActionTypes.ExportSheetCompleted,
)

export const presentCreateSheetModalRequested = createAction(
	SheetActionTypes.PresentCreateSheetModalRequested,
	props<{ visitId: string }>()
)

export const createSheetTemplateFromSheetRequested = createAction(
	SheetActionTypes.CreateSheetTemplateFromSheetRequested,
	props<{ sheetId: string, templateName: string }>()
)

export const createSheetTemplateFromSheetCompleted = createAction(
	SheetActionTypes.CreateSheetTemplateFromSheetCompleted,
	props<{ template: SheetTemplate }>()
)

export const presentCreateSheetTemplateModal = createAction(
	SheetActionTypes.PresentCreateSheetTemplateModal,
	props<{ sheetId: string }>()
)

export const presentManageSheetTemplatesModalRequested = createAction(
	SheetActionTypes.PresentManageSheetTemplatesModalRequested
)

export const presentItemSearchModalRequested = createAction(
	SheetActionTypes.PresentItemSelectorRequested
)

export const presentItemSearchModalAllowed = createAction(
	SheetActionTypes.PresentItemSearchModalAllowed
)

export const loadSheetItemEditorRequested = createAction(
	SheetActionTypes.LoadSheetItemEditorRequested,
	props<{ item: Row }>()
)

export const loadSheetItemEditorCompleted = createAction(
	SheetActionTypes.LoadSheetItemEditorCompleted
)

export const addSheetItemInstructionsRequested = createAction(
	SheetActionTypes.AddSheetItemInstructionsRequested,
	props<{ instructions: Instruction[], itemProperties: ItemProperties, actionAlerts: ActionAlert[] | null }>()
)

export const addSheetItemInstructionsCompleted = createAction(
	SheetActionTypes.AddSheetItemInstructionsCompleted
)

export const setItemPropertiesRequested = createAction(
	SheetActionTypes.SetItemPropertiesRequested,
	props<{ sheetId: string, itemId: number, itemProperties: ItemProperties }>()
)

export const setItemPropertiesCompleted = createAction(
	SheetActionTypes.SetItemPropertiesCompleted
)

export const updateSheetItemActionAlertsRequested = createAction(
	SheetActionTypes.UpdateSheetItemActionAlertsRequested,
	props<{ sheetId: string, itemId: number, actionAlerts: ActionAlert[] }>()
)

export const updateSheetItemActionAlertsCompleted = createAction(
	SheetActionTypes.UpdateSheetItemActionAlertsCompleted
)

export const updateSheetItemInstructionsRequested = createAction(
	SheetActionTypes.UpdateSheetItemInstructionsRequested,
	props<{ itemId: number, instructions: Instruction[], itemProperties: ItemProperties, actionAlerts: ActionAlert[] | null }>()
)

export const updateSheetItemInstructionsCompleted = createAction(
	SheetActionTypes.UpdateSheetItemInstructionsCompleted
)

export const loadSheetSlotEditorRequested = createAction(
	SheetActionTypes.LoadSheetSlotEditorRequested,
	props<{ itemId: number, slotIndex: number }>()
)

export const loadSheetSlotEditorCompleted = createAction(
	SheetActionTypes.LoadSheetSlotEditorCompleted
)

export const saveSheetSlotActionRequested = createAction(
	SheetActionTypes.SaveSheetSlotActionRequested,
	props<{ rowId: number, action: Action, isAddition: boolean }>()
)

export const addSheetSlotActionCompleted = createAction(
	SheetActionTypes.AddSheetSlotActionCompleted,
	props<{ actionId: number }>()
)

export const updateSheetSlotActionCompleted = createAction(
	SheetActionTypes.UpdateSheetSlotActionCompleted
)

export const selectDayRequested = createAction(
	SheetActionTypes.SelectDayRequested,
	props<{ date: string }>()
)

export const selectDayCompleted = createAction(
	SheetActionTypes.SelectDayCompleted
)

export const updateScheduledUntilDateRequested = createAction(
	SheetActionTypes.UpdateScheduledUntilDateRequested,
	props<{ sheetId: string, requestedDate: string }>()
)

export const updateScheduledUntilDateCompleted = createAction(
	SheetActionTypes.UpdateScheduledUntilDateCompleted,
	props<{ sheetEndDate: string }>()
)

export const loadSheetActionNotesRequested = createAction(
	SheetActionTypes.LoadSheetActionNotesRequested,
	props<{ sheetId: string }>()
)

export const loadSheetActionNotesCompleted = createAction(
	SheetActionTypes.LoadSheetActionNotesCompleted,
	props<{ actionNotes: SheetActionNote[] }>()
)

export const changeViewStatusRequested = createAction(
	SheetActionTypes.ChangeViewStatusRequested,
	props<{ viewSelector: SheetViewSelector }>()
)

export const changeViewStatusCompleted = createAction(
	SheetActionTypes.ChangeViewStatusCompleted,
	props<{ viewStatus: SheetViewStatus }>()
)

export const changeGranularityRequested = createAction(
	SheetActionTypes.ChangeGranularityRequested,
	props<{ granularity: SheetGranularity, slotNumber: number }>()
)

export const changeGranularityCompleted = createAction(
	SheetActionTypes.ChangeGranularityCompleted,
	props<{ granularity: SheetGranularity }>()
)

export const sheetActionNotesCleared = createAction(
	SheetActionTypes.SheetActionNotesCleared
)

export const removeSheetItemRequested = createAction(
	SheetActionTypes.RemoveSheetItemRequested,
	props<{ sheetId: string, itemId: number }>()
)

export const removeSheetItemCompleted = createAction(
	SheetActionTypes.RemoveSheetItemCompleted
)

export const sheetCleared = createAction(
	SheetActionTypes.SheetCleared
)